import { Col, Row } from 'antd'
import classNames from 'classnames'
import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyIcon } from '~/components/ui/MyIcon'
import { MyModal } from '~/components/ui/MyModal'
import {
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_modelFusionOptions,
  getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_recommendedModelFusionOptions,
} from '~/graphqls/types'
import { uniqueFunc } from '~/utils'
import Stepper from '../Stepper'
import styles from './ModelFusionSelect.module.css'
import { OptionsTabs } from './OptionsTabs'

export interface ModelFusionDataProps {
  key: string
  weight: number
}

type ModelFusionOptionsProps =
  Partial<getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_modelFusionOptions>

/** 默认显示数量 */
const DEFAULT_SHOW_COUNT = 7

const ModelFusionItem: React.FC<{
  isActive: boolean
  isDisabled: boolean
  onChange: (val: number) => void
  onChecked: () => void
  data: ModelFusionOptionsProps
}> = ({ isActive, isDisabled, onChange, onChecked, data }) => {
  return (
    <div
      className={classNames(
        'relative rounded-lg p-1 h-[72px] text-neutral-800 font-semibold text-sm border-2 text-center cursor-pointer flex justify-center items-center',
        [[isActive ? 'border-neutral-500' : 'border-transparent']],
        {
          'cursor-not-allowed': isDisabled && !isActive,
        }
      )}
      style={{
        background: isActive
          ? 'radial-gradient(74.76% 334.54% at 49.84% 50%, #374176 0%, rgba(55, 65, 118, 0) 100%)'
          : '#23262F',
      }}
      onClick={onChecked}
    >
      {data?.isNew && (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/new.png"
          alt=""
        />
      )}
      {isActive ? (
        <div>
          <div className="mb-1.5">{data.name}</div>
          <Stepper
            className={styles.stepper}
            step={0.05}
            min={-1}
            max={2}
            digits={2}
            value={data.weight!}
            onChange={val => onChange(val)}
          />
        </div>
      ) : (
        `${data.name}`
      )}
    </div>
  )
}

export const ModelFusionSelect: React.FC<{
  current: ModelFusionDataProps[]
  setCurrent: Dispatch<SetStateAction<ModelFusionDataProps[]>>
  recommendedOptions: getAIMenu_pictureGraph_aiCreateArtworkOptionMenu_recommendedModelFusionOptions[]
  options: ModelFusionOptionsProps[]
  max?: number
}> = ({ current, setCurrent, options, recommendedOptions, max = 5 }) => {
  const [visible, setVisible] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const tabs = Array.from(
    new Set(
      options?.filter(item => !!item.category)?.map(item => item.category!)
    )
  )

  const isDisabled = current.length >= max && max !== 1

  const onChange = useCallback((item: ModelFusionOptionsProps, val: number) => {
    setCurrent(current =>
      current.map(it => {
        if (it.key === item.key) {
          return {
            ...it,
            weight: val,
          }
        } else {
          return it
        }
      })
    )
  }, [])

  const onChecked = useCallback(
    (item: ModelFusionOptionsProps, isActive: boolean) => {
      if (isActive) {
        setCurrent(current => current.filter(it => it.key !== item.key))
      } else {
        if (current.length >= max) return

        setCurrent(current => [
          ...current,
          {
            key: item.key!,
            weight: item.weight!,
          },
        ])
      }
    },
    [current, max]
  )

  const defaultOptions = uniqueFunc(
    [
      ...current.map(item => {
        return {
          ...options.find(o => o.key === item.key),
          weight: item.weight,
        }
      }),
      ...recommendedOptions?.slice(0, DEFAULT_SHOW_COUNT),
    ],
    'key'
  )

  return (
    <>
      <Row gutter={12}>
        {defaultOptions.map(item => {
          const currentItem = current.find(
            it => (it.key as string) === item.key
          )
          const isActive =
            current.findIndex(it => (it.key as string) === item.key) > -1
          return (
            <Col key={item.key} span={6} className="mb-3">
              <ModelFusionItem
                isActive={isActive}
                isDisabled={isDisabled}
                data={{ ...item, ...currentItem }}
                onChecked={() => {
                  onChecked(item, isActive)
                }}
                onChange={val => {
                  onChange(item, val)
                }}
              />
            </Col>
          )
        })}
        <Col span={6} className="mb-3">
          <div
            className="rounded-lg p-1 h-[72px] text-neutral-800 font-semibold text-sm border-2 border-neutral-300 text-center cursor-pointer flex items-center justify-center"
            style={{
              background: 'rgba(35, 38, 47, 0.32)',
            }}
            onClick={() => setVisible(true)}
          >
            <MyIcon icon={'more1'} className="font-normal" />
            <span className="ml-0.5">更多</span>
          </div>
        </Col>
      </Row>
      <MyModal
        open={visible}
        title={
          <>
            融合模型 ({current.length}/{max})
          </>
        }
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={720}
        footer={null}
      >
        <OptionsTabs
          options={tabs}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />
        <Row gutter={12} className="mb-3 max-h-[400px] overflow-auto scrollbar">
          {options
            .filter(
              item => !item.category || item.category === tabs[currentIndex]
            )
            .map(item => {
              const currentItem = current.find(
                it => (it.key as string) === item.key
              )
              const isActive =
                current.findIndex(it => (it.key as string) === item.key) > -1
              return (
                <Col key={item.key} span={6} className="mb-3">
                  <ModelFusionItem
                    isActive={isActive}
                    isDisabled={isDisabled}
                    data={{ ...item, ...currentItem }}
                    onChecked={() => {
                      onChecked(item, isActive)
                    }}
                    onChange={val => {
                      onChange(item, val)
                    }}
                  />
                </Col>
              )
            })}
        </Row>
        <div className="flex justify-between items-center gap-3">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setCurrent([])
              setVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
